import { Grid, Typography } from '@mui/material';
import { useModal } from 'mui-modal-provider';
import React, { useEffect, useState } from 'react';
import {
  CardProps,
  RootineCard,
  RootineTable,
  RowProps,
  TableProps,
} from '../../../atoms';
import { CHECKOUT_URLS, PRODUCT_CODE_MAPPING } from '../../../config/index';
import { APIRequests } from '../../../config/legacyapi';
import {
  BloodResult,
  RequestInput,
  UserBloodResultsOut,
  UserGetIn,
} from '../../../types';
import { useGlobalState } from '../../../utils/GlobalStateProvider';
import useLegacyAPI from '../../../utils/useLegacyApi';
import userServices from '../../../utils/user';
import { Cart, CartProps } from '../../Cart';
import { Loading } from '../../Layout/Loading';

export const BloodResults = (): JSX.Element => {
  const { showModal } = useModal();

  // need to call this to get products in state for cart
  const context = useGlobalState(true);
  const [minerals, setMinerals] = useState<BloodResult[]>([]);
  const [vitamins, setVitamins] = useState<BloodResult[]>([]);
  const [hasVitamins, setHasVitamins] = useState<boolean>(true);
  const [hasMinerals, setHasMinerals] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<boolean>(false);

  let bloodResultData = useLegacyAPI<UserBloodResultsOut>(
    APIRequests.UserBloodResultsList
  );
  const userEmail = userServices.getEmail();
  const userToken = userServices.getToken();

  const rdata: UserGetIn = {
    email: userEmail,
    token: userToken,
  };

  const req: RequestInput = { queryParams: rdata };

  useEffect(() => {
    bloodResultData.execute(req);
  }, []);

  useEffect(() => {
    if (
      bloodResultData.data &&
      bloodResultData.data.user_vitamins &&
      bloodResultData.data.user_vitamins.length
    ) {
      const results = bloodResultData.data.user_vitamins;
      let vitamins = results.filter(
        (result) => result.nutrient_group === 'vitamins'
      );
      // Override b6 because we no longer analyze it
      const b6 = vitamins.filter(
        (result) => result.nutrient_name === 'Vitamin B6'
      )[0];
      if (b6?.nutrient_value === 'unavailable') {
        vitamins.splice(vitamins.indexOf(b6), 1);
      }
      const unavailableVitamins = vitamins.filter(
        (result) => result.nutrient_value === 'unavailable'
      );
      const minerals = results.filter(
        (result) => result.nutrient_group === 'minerals'
      );
      const unavailableMinerals = minerals.filter(
        (result) => result.nutrient_value === 'unavailable'
      );
      // this logic is in place because older vitamin test had
      // some of the same, but some missing analytes
      setHasVitamins(vitamins.length !== unavailableVitamins.length);
      setHasMinerals(minerals.length !== unavailableMinerals.length);
      setVitamins(vitamins);
      setMinerals(minerals);
      setLoaded(true);
    }
  }, [bloodResultData.data]);

  const addToCart = () => {
    const product: string[] = [];
    if (!hasMinerals && !hasVitamins) {
      product.push(PRODUCT_CODE_MAPPING.blood_bundle); // bundle
    } else if (!hasMinerals) {
      product.push(PRODUCT_CODE_MAPPING.bloodmineral);
    } else {
      product.push(PRODUCT_CODE_MAPPING.bloodvitamin);
    }
    const cartProps: CartProps = {
      products: product,
      bypassModal: false,
    };
    showModal(Cart, { ...cartProps });
  };
  const columns = {
    nutrient_name: { name: 'ANALYTE' },
    nutrient_value_unit: { name: 'RESULT' },
  };

  const rows = (analytes: BloodResult[]): { [name: string]: RowProps }[] => {
    return analytes.map((analyte) => ({
      nutrient_name: { value: analyte.nutrient_name, bold: true },
      nutrient_value_unit: {
        value: analyte.nutrient_value_unit,
        sx: { color: 'info.main' },
      },
    }));
  };

  const cardContent = (type: string): JSX.Element => {
    const tableProps: TableProps = {
      tableName: 'analytes table',
      columns: columns,
      rows: type === 'minerals' ? rows(minerals) : rows(vitamins),
      selectedColor: 'white',
    };

    return (
      <>
        {loaded && <RootineTable {...tableProps} />}
        {!loaded && <Loading />}
      </>
    );
  };

  const vitaminCardProps: CardProps = {
    title: 'BLOOD VITAMIN RESULTS',
    content: cardContent('vitamins'),
    cta: {
      btnId: 'bld-vit-popup',
      text: hasVitamins ? undefined : CHECKOUT_URLS.blood_test.text,
      subText: hasVitamins ? undefined : 'Results within 5 days',
      func: addToCart,
    },
  };

  const mineralCardProps: CardProps = {
    title: 'BLOOD MINERAL RESULTS',
    content: cardContent('minerals'),
    cta: {
      btnId: 'bld-min-popup',
      text: hasMinerals ? undefined : CHECKOUT_URLS.blood_mineral_test.text,
      subText: hasMinerals ? undefined : 'Results within 5 days',
      func: addToCart,
    },
  };

  return (
    <>
      <Typography variant="h1" sx={{ fontSize: 24, textAlign: 'center' }}>
        {' '}
        View your results
      </Typography>
      <Grid container xs={12} paddingX={2}>
        <RootineCard {...vitaminCardProps} />
        <RootineCard {...mineralCardProps} />
      </Grid>
    </>
  );
};
